<template>
  <div>

    <v-app v-if="mobConfig">
      <v-navigation-drawer location="right" v-model="drawer" temporary>
        <v-list-item>
          <v-list-item-title class="title">
            <span v-html="$safeHtml(title)"></span>
          </v-list-item-title>
          <v-list-item-subtitle>
            <span v-html="$safeHtml(mobConfig.companyName)"></span>
          </v-list-item-subtitle>
        </v-list-item>

        <v-divider></v-divider>

        <v-list density="compact" nav>
          <v-list-item v-if="mobConfig.enabledSchool" to="/course" :prepend-icon="mobSchoolIcon">
            <v-list-item-title><span v-html="$safeHtml(schoolHeader)"></span></v-list-item-title>
          </v-list-item>

          <v-list-item v-if="mobConfig.enabledRent" to="/rent" :prepend-icon="mobRentIcon">
            <v-list-item-title><span v-html="$safeHtml(rentHeader)"></span></v-list-item-title>
          </v-list-item>

          <v-list-item to="/cart" prepend-icon="mdi-cart">
            <v-list-item-title>{{ $t("Košík") }}</v-list-item-title>
          </v-list-item>

          <template v-for="sc in visibleStaticContent" :key="sc.id">
            <v-list-item
              v-if="!sc.isExternal"
              :to="'/static-content/' + sc.id + '/' + sc.linkUrl"
              :prepend-icon="sc.icon"
            >
              <v-list-item-title>
                <span v-html="$safeHtml(translateFromPayload(sc.title))"></span>
              </v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="sc.isExternal"
              :href="$safeUrl(sc.linkUrl)"
              target="_blank"
              :prepend-icon="sc.icon"
            >
              <v-list-item-title>
                <span v-html="$safeHtml(translateFromPayload(sc.title))"></span>
              </v-list-item-title>
            </v-list-item>
          </template>

          <locale-select variant="list"></locale-select>
        </v-list>

        <v-divider></v-divider>

        <v-spacer></v-spacer>

        <template v-slot:append>
          <div class="pa-2">
            <div class="text-caption text-center">
              {{ $t("Vytvořeno na platformě") }}:
            </div>
            <v-btn block variant="text" size="small" @click="navigateToMatesWeb">
              www.is-mates.cz
            </v-btn>
            <p class="app-version">
              {{ appVersion }} / {{ mobConfig.mobVersion }}
            </p>
          </div>
        </template>
      </v-navigation-drawer>

      <v-app-bar>
        <router-link to="/">
          <v-img
            :alt="logoAlt"
            class="shrink mr-2 d-none d-sm-block"
            contain
            :src="mobConfig.webLogoUrl"
            transition="scale-transition"
            height="43"
            width="200"
          />

          <v-img
            :alt="logoAlt"
            class="shrink mr-2 d-sm-none"
            contain
            :src="mobConfig.webLogoSmallUrl"
            transition="scale-transition"
            height="40"
            width="40"
          />
        </router-link>

        <v-tabs
          v-model="tabsModel"
          v-if="
            mdAndUp &&
            mobConfig.enabledSchool &&
            mobConfig.enabledRent
          "
        >
          <v-tab @click="navigateToSchool"><span v-html="$safeHtml(schoolHeader)"></span></v-tab>
          <v-tab @click="navigateToRent"><span v-html="$safeHtml(rentHeader)"></span></v-tab>
        </v-tabs>
        <v-toolbar-title v-else class="d-none d-md-block">
          <span v-html="$safeHtml(title)"></span>
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <locale-select></locale-select>

        <v-btn rounded="pill" variant="elevated" color="surface" class="ml-2 mr-5" to="/cart">
          <v-icon start>mdi-cart</v-icon>
          <span class="d-none d-sm-inline"> {{ $t("Košík") }}</span>
          <template v-if="orderr">
            &nbsp;|
            <CartCountdown :end-time="orderrExpirationTime" @finish="orderrExpirationHandler">
              <template #process="{ timeObj }">
                &nbsp;{{ `${timeObj.m}:${timeObj.s}` }}
              </template>
              <template #finish>
                <span>&nbsp;{{ $t("Čas vypršel") }}</span>
              </template>
            </CartCountdown>
          </template>
        </v-btn>

        <v-app-bar-nav-icon :aria-label="$t('Menu')" @click="drawer = true"></v-app-bar-nav-icon>
      </v-app-bar>

      <v-main>
        <v-container fluid class="pa-0 main-container" style="height: 100%">
          <router-view />
        </v-container>

        <!-- MOB-043: pobocka rozpracovane objednavky byla vyrazena z online prodeje.
             Kosik na ni nelze dokoncit, dialog je persistent a jedina akce zalozi novou
             objednavku a vrati klienta na volbu pobocky. -->
        <v-row justify="center">
          <v-dialog
            :model-value="pointSellDisabledDialog"
            persistent
            max-width="550"
            data-testid="point-sell-disabled-dialog"
          >
            <v-card>
              <v-card-title class="text-h6">
                <v-icon color="error"> mdi-alert-circle </v-icon>
                {{ $t("pobockaJizNeniOnline") }}
              </v-card-title>
              <v-card-text>{{ $t("pobockaJizNeniOnlinePopis") }}</v-card-text>
              <v-card-actions>
                <v-btn
                  @click="resetOrderrWithDisabledPointSell()"
                  variant="text"
                  data-testid="point-sell-disabled-dialog-reset"
                >
                  {{ $t("Vytvořit novou objednávku") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-row>

        <!-- notifikace - expirace objednvky -->
        <v-row justify="center">
          <v-dialog v-model="expirationSnackbar" persistent max-width="550">
            <v-card>
              <v-card-title class="text-h6">
                <v-icon color="error"> mdi-alert-circle </v-icon>
                {{ $t("vyprselCasProDokonceni") }}
              </v-card-title>
              <v-card-text>{{ $t("nesloDeleBlokovat") }} </v-card-text>
              <v-card-actions>
                <v-btn @click="resetExpiredOrerr()" variant="text">
                  {{ $t("Vytvořit novou objednávku") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-row>
      </v-main>
    </v-app>
  </div>
</template>

<script>
import moment from "moment";
import { useDisplay } from "vuetify";
import { useHead } from "@unhead/vue";
import LocaleSelect from "@/components/LocaleSelect";
import CartCountdown from "@/components/CartCountdown";
import LocaleUtil from "@/libs/LocaleUtil";
import { plainText } from "@/libs/HtmlUtil";
import { APP_LOCALES } from "@/libs/LanguageUtil";
import appPackage from "../package.json";
import { setOptions, bootstrap } from "vue-gtag";

export default {
  name: "app",

  components: {
    LocaleSelect,
    CartCountdown,
  },

  setup() {
    const { mdAndUp } = useDisplay();
    useHead({
      titleTemplate: (t) => (t ? `${t} | Online rezervace` : "Online rezervace"),
    });
    return { mdAndUp };
  },

  data() {
    return {
      tabsModel: null,
      drawer: false,
      expirationAlertSnackbar: false,
      expirationSnackbar: false,
      snackbarTimeout: 5000,
    };
  },

  watch: {
    $route(to) {
      console.log("zmena routy ")
      console.log(to.path);
      let path = to.path;
      if(to != null) {
        if (path.includes("/course") > 0) {
        this.tabsModel = 0;
      } else if (path.includes("/rent") > 0) {
        this.tabsModel = 1;
      }
      }
      
    },
  },

  computed: {
    mobRentIcon() {
      return this.$store.getters.mobRentIcon;
    },

    mobSchoolIcon() {
      return this.$store.getters.mobSchoolIcon;
    },

    mobConfig() {
      return this.$store.state.mobConfig;
    },

    title() {
      return LocaleUtil.translateFromPayload(
        this.mobConfig.title,
        this.$i18n.locale
      );
    },

    // The title is a Rich operator field: it can carry tags and entities. An alt attribute
    // cannot show HTML, so it holds the text (MOB-461, doctrine E87).
    logoAlt() {
      return plainText(this.title);
    },

    schoolHeader() {
      return LocaleUtil.translateFromPayload(
        this.mobConfig.schoolHeader,
        this.$i18n.locale
      );
    },

    rentHeader() {
      return LocaleUtil.translateFromPayload(
        this.mobConfig.rentHeader,
        this.$i18n.locale
      );
    },

    appVersion() {
      return appPackage.version;
    },

    orderrExpirationTime() {
      return moment(this.$store.state.expireAt).toDate();
    },

    orderrExpirationAlertTime() {
      return moment(this.$store.state.expireAt).subtract(5, "minutes").toDate();
    },

    orderr() {
      return this.$store.state.currentOrderr;
    },

    // MOB-043: raised by getOrderr when a DRAFT is bound to a branch that has since been
    // put into `mobConfig.rentDisabledPointSells`. The expiration dialog wins when both
    // apply — an expired cart is gone either way.
    pointSellDisabledDialog() {
      return (
        this.$store.state.currentOrderrPointSellDisabled && !this.expirationSnackbar
      );
    },

    staticContentList() {
      return this.$store.state.staticContentList;
    },

    // Drawer entries need a title in the active locale, and an external entry
    // needs a real URL — tenant data contains placeholder rows (literal "LLL")
    // that otherwise render as blank, dead list items.
    visibleStaticContent() {
      return this.staticContentList.filter((sc) => {
        const title = this.translateFromPayload(sc.title);
        if (!title || plainText(title) === "") {
          return false;
        }
        if (sc.isExternal) {
          return typeof sc.linkUrl === "string" && /^https?:\/\//.test(sc.linkUrl);
        }
        return true;
      });
    },
  },

  methods: {

    enableGtag() {
      if (!import.meta.env.PROD) return;

      const centralId = import.meta.env.VITE_GA_CENTRAL_ID;
      const tenantId = this.mobConfig?.gaTrackingId;

      if (!centralId && !tenantId) return;

      const primaryId = centralId || tenantId;
      const options = {
        config: { id: primaryId }
      };

      if (centralId && tenantId && centralId !== tenantId) {
        options.includes = [{ id: tenantId }];
      }

      setOptions(options);
      bootstrap();
    },

    translateFromPayload(payload) {
      return LocaleUtil.translateFromPayload(payload, this.$i18n.locale);
    },

    navigateToMatesWeb: function () {
      window.location.href = "http://www.ismates.cz";
    },

    async init() {
      console.log("app.vue -- init start");

      await this.$store.dispatch("loadMobConfig");
      this.$store.dispatch("loadLocations");
      this.$store.dispatch("loadPointSells");
      this.$store.dispatch("loadPointMeetings");
      this.$store.dispatch("loadStaticContentList");
      this.$store.dispatch("loadRentProductRequirements");

      // Apply default locale from config
      let defaultLocale = this.$store.getters.defaultLocale;
      this.$i18n.locale = defaultLocale;
      this.$store.dispatch("localeChange", defaultLocale);

      if(this.$route.query.lang != undefined
          && this.$route.query.lang != null
          && APP_LOCALES.includes(this.$route.query.lang)
      ) {
        this.$i18n.locale = this.$route.query.lang;
        this.$store.dispatch("localeChange", this.$route.query.lang);
      }
      

      // jestlize je stale null, pokus o nacteni z cookies
      if (this.orderr == null) {
        console.log("Objednavka je NULL");
        let id = this.$cookies.get("orderrId");
        if (id != undefined && id != null) {
          console.log("Nalezen cookie s orderrId:" + id);
          this.$store.dispatch("getOrderr", id);
          console.log("Nacteno z cookie");
        }
      }

      console.log("app.vue -- init done");
    },

    orderrExpirationHandler() {
      console.log("orderrExpirationHandler");
      this.expirationAlertSnackbar = false;
      this.expirationSnackbar = true;

      this.$store.dispatch("updateCurrentOrderrExpired", true);
    },

    async resetExpiredOrerr() {
      // expirace aktualni objednavky - zaslat update na backend

      // reset lokalnich promennych
      await this.$store.dispatch("expireOrderr");

      // schovani modalniho okna
      this.expirationSnackbar = false;

      // presmerovani
      this.navigateToHome();
    },

    // MOB-043: drop the cart bound to the branch that is no longer bookable online and let
    // the client choose again. Mirrors resetExpiredOrerr() — including navigateToHome(),
    // which honours enabledSchool/enabledRent instead of assuming the rental section is on.
    async resetOrderrWithDisabledPointSell() {
      await this.$store.dispatch("discardOrderrWithDisabledPointSell");
      this.navigateToHome();
    },

    navigateToHome() {
      if (this.mobConfig.enabledSchool && !this.mobConfig.enabledRent) {
        this.$router.push({ name: "course", query: {lang: this.$i18n.locale} });
      } else if (!this.mobConfig.enabledSchool && this.mobConfig.enabledRent) {
        this.$router.push({ name: "rent", query: {lang: this.$i18n.locale} });
      } else if (this.mobConfig.enabledSchool && this.mobConfig.enabledRent) {
        this.$router.push({ name: "rent", query: {lang: this.$i18n.locale} });
      } else {
        console.error("Chyba - neni definovana ani sekce pujcvna, ani skola.");
      }
    },

    navigateToSchool() {
      this.$router.push({ name: "course" , query: {lang: this.$i18n.locale}});
    },

    navigateToRent() {
      this.$router.push({ name: "rent" , query: {lang: this.$i18n.locale}});
    },
  },

  async mounted() {
    console.log("mounted -- App.vue");
    // init() resolves after loadMobConfig, so enableGtag() sees the per-tenant
    // gaTrackingId; calling it before init() finished always missed the tenant id.
    await this.init();
    this.enableGtag();
  },
};
</script>

<style>
body {
  margin: 0;
}
#app {
  /* Font family and text color come from the Vuetify theme
     (src/styles/settings.scss + src/plugins/vuetify.js). */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.signout {
  background-color: rgb(var(--v-theme-surface-variant));
  margin: 0;
  padding: 11px 0px 1px;
}
div.auth-wrapper {
  position: fixed;
  z-index: 100;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  overflow: auto;
}

amplify-authenticator div.error-msg {
  border: solid 1px rgb(var(--v-theme-error));
  background-color: rgba(var(--v-theme-error), 0.12);
  color: rgb(var(--v-theme-on-surface));
  padding: 1rem;
}

amplify-authenticator div.dialog-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
}

amplify-authenticator::shadow amplify-toast {
  position: fixed;
  top: 40%;
  left: 10%;
  right: 10%;
  z-index: 200;
}

.app-version {
  text-align: center;
  margin: 1rem;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}
</style>
